import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Layers, Activity, Shield, User, RefreshCw, ChevronRight, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const MODE_COLORS = {
  PREDECODE: { bg: 'bg-slate-800/60', text: 'text-slate-300', badge: 'bg-slate-700 text-slate-200' },
  DECODE_PENDING: { bg: 'bg-blue-900/40', text: 'text-blue-300', badge: 'bg-blue-800 text-blue-200' },
  DECODE_COMPLETE: { bg: 'bg-cyan-900/40', text: 'text-cyan-300', badge: 'bg-cyan-800 text-cyan-200' },
  THERAPEUTIC_CORRECTION: { bg: 'bg-rose-900/40', text: 'text-rose-300', badge: 'bg-rose-800 text-rose-200' },
  BASELINE_ACHIEVED: { bg: 'bg-amber-900/40', text: 'text-amber-300', badge: 'bg-amber-800 text-amber-200' },
  PERFORMATIVE: { bg: 'bg-emerald-900/40', text: 'text-emerald-300', badge: 'bg-emerald-800 text-emerald-200' },
};

const MODE_LABELS = {
  PREDECODE: 'Pre-DECODE', DECODE_PENDING: 'DECODE Pending', DECODE_COMPLETE: 'DECODE Complete',
  THERAPEUTIC_CORRECTION: 'Therapeutic Correction', BASELINE_ACHIEVED: 'Baseline Achieved', PERFORMATIVE: 'Performative',
};

const ENTRY_LABELS = { SEEDS: 'Seeds', ROOTS: 'Roots', ETERNIA: 'Eternia', THERAPEUTICS: 'Therapeutics' };
const PERF_LABELS = { SEEDS: 'Seeds', ROOTS: 'Roots', ETERNIA: 'Eternia' };

function SubscriberCard({ sub, onRefresh }) {
  const [dialog, setDialog] = useState(null); // 'mode' | 'entry' | 'perf' | 'therapeutic'
  const [selected, setSelected] = useState('');
  const [saving, setSaving] = useState(false);

  const mc = MODE_COLORS[sub.current_mode] || MODE_COLORS.PREDECODE;
  const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };

  const save = async (endpoint, payload) => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/journey/subscriber/${sub.subscriber_id}/${endpoint}`, payload, { headers });
      toast.success('Updated successfully');
      setDialog(null);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };

  return (
    <Card data-testid={`journey-card-${sub.subscriber_id}`} className={`${mc.bg} border border-white/10`}>
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
              <User className="w-3.5 h-3.5 text-white/60" />
            </div>
            <div>
              <p data-testid={`journey-name-${sub.subscriber_id}`} className="text-sm font-semibold text-white">{sub.name || sub.subscriber_id}</p>
              <p className="text-[10px] text-white/40">{sub.conditions?.join(', ')}</p>
            </div>
          </div>
          <Badge data-testid={`journey-mode-badge-${sub.subscriber_id}`} className={`${mc.badge} text-[10px]`}>
            {MODE_LABELS[sub.current_mode] || sub.current_mode}
          </Badge>
        </div>

        {/* 4-Field Grid */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          {/* Entry Pathway */}
          <div className="bg-black/20 rounded p-2.5">
            <p className="text-[9px] text-white/30 uppercase tracking-wider">Entry Pathway</p>
            <div className="flex items-center justify-between mt-1">
              <p data-testid={`journey-entry-${sub.subscriber_id}`} className="text-xs font-medium text-white/80">
                {ENTRY_LABELS[sub.entry_pathway] || sub.entry_pathway || '—'}
              </p>
              <button data-testid={`journey-edit-entry-${sub.subscriber_id}`}
                className="text-[9px] text-white/30 hover:text-white/60"
                onClick={() => { setSelected(sub.entry_pathway || ''); setDialog('entry'); }}>
                Edit
              </button>
            </div>
          </div>

          {/* Current Mode */}
          <div className="bg-black/20 rounded p-2.5">
            <p className="text-[9px] text-white/30 uppercase tracking-wider">Current Mode</p>
            <div className="flex items-center justify-between mt-1">
              <p data-testid={`journey-mode-${sub.subscriber_id}`} className={`text-xs font-medium ${mc.text}`}>
                {MODE_LABELS[sub.current_mode] || sub.current_mode}
              </p>
              <button data-testid={`journey-edit-mode-${sub.subscriber_id}`}
                className="text-[9px] text-white/30 hover:text-white/60"
                onClick={() => { setSelected(''); setDialog('mode'); }}>
                Change
              </button>
            </div>
            {sub.valid_next_modes?.length > 0 && (
              <p className="text-[9px] text-white/20 mt-1">
                Next: {sub.valid_next_modes.map(m => MODE_LABELS[m]).join(', ')}
              </p>
            )}
          </div>

          {/* Therapeutic State */}
          <div className="bg-black/20 rounded p-2.5">
            <p className="text-[9px] text-white/30 uppercase tracking-wider">Therapeutic</p>
            <div className="flex items-center justify-between mt-1">
              <div>
                <p data-testid={`journey-therapeutic-${sub.subscriber_id}`}
                  className={`text-xs font-medium ${sub.is_therapeutic_active ? 'text-rose-400' : 'text-emerald-400'}`}>
                  {sub.therapeutic_state}
                </p>
                {sub.therapeutic_intensity && (
                  <p className="text-[9px] text-white/25">{sub.therapeutic_intensity}</p>
                )}
              </div>
              <button data-testid={`journey-toggle-therapeutic-${sub.subscriber_id}`}
                className="text-[9px] text-white/30 hover:text-white/60"
                onClick={() => { setDialog('therapeutic'); }}>
                Toggle
              </button>
            </div>
          </div>

          {/* Performative Pathway */}
          <div className="bg-black/20 rounded p-2.5">
            <p className="text-[9px] text-white/30 uppercase tracking-wider">Performative Pathway</p>
            <div className="flex items-center justify-between mt-1">
              <p data-testid={`journey-perf-${sub.subscriber_id}`} className="text-xs font-medium text-white/80">
                {sub.performative_pathway ? PERF_LABELS[sub.performative_pathway] : '(not assigned)'}
              </p>
              <button data-testid={`journey-edit-perf-${sub.subscriber_id}`}
                className="text-[9px] text-white/30 hover:text-white/60"
                onClick={() => { setSelected(sub.performative_pathway || ''); setDialog('perf'); }}>
                Assign
              </button>
            </div>
          </div>
        </div>

        {/* Deprecated fields (dimmed) */}
        <div className="flex items-center gap-3 text-[9px] text-white/15 border-t border-white/5 pt-2">
          <span>legacy journey_stage: <span className="font-mono">{sub.journey_stage}</span></span>
          <span>life_stage: <span className="font-mono">{sub.life_stage}</span></span>
          <Badge variant="outline" className="text-[7px] text-white/15 border-white/10 px-1">deprecated</Badge>
        </div>

        {/* Dialogs */}
        {/* Mode Change */}
        <Dialog open={dialog === 'mode'} onOpenChange={(o) => !o && setDialog(null)}>
          <DialogContent className="bg-[#1a1a2e] border-white/10 text-white">
            <DialogHeader><DialogTitle>Change Mode</DialogTitle></DialogHeader>
            <div className="py-3">
              <p className="text-xs text-white/50 mb-2">
                Current: <span className={mc.text}>{MODE_LABELS[sub.current_mode]}</span>
                {sub.valid_next_modes?.length > 0 && ` → Valid next: ${sub.valid_next_modes.map(m => MODE_LABELS[m]).join(', ')}`}
              </p>
              <Select value={selected} onValueChange={setSelected}>
                <SelectTrigger data-testid="journey-mode-select" className="bg-black/30 border-white/10 text-white">
                  <SelectValue placeholder="Select mode..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a2e] border-white/10">
                  {['PREDECODE','DECODE_PENDING','DECODE_COMPLETE','THERAPEUTIC_CORRECTION','BASELINE_ACHIEVED','PERFORMATIVE'].map(m => (
                    <SelectItem key={m} value={m} className="text-white">
                      {MODE_LABELS[m]}
                      {sub.valid_next_modes?.includes(m) && ' (valid)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDialog(null)} className="text-white/60">Cancel</Button>
              <Button data-testid="journey-save-mode" onClick={() => save('mode', { current_mode: selected })}
                disabled={saving || !selected} className="bg-blue-600 hover:bg-blue-700">
                {saving ? 'Saving...' : 'Update'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Entry Pathway */}
        <Dialog open={dialog === 'entry'} onOpenChange={(o) => !o && setDialog(null)}>
          <DialogContent className="bg-[#1a1a2e] border-white/10 text-white">
            <DialogHeader><DialogTitle>Set Entry Pathway</DialogTitle></DialogHeader>
            <div className="py-3">
              <Select value={selected} onValueChange={setSelected}>
                <SelectTrigger data-testid="journey-entry-select" className="bg-black/30 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a2e] border-white/10">
                  {['SEEDS','ROOTS','ETERNIA','THERAPEUTICS'].map(p => (
                    <SelectItem key={p} value={p} className="text-white">{ENTRY_LABELS[p]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDialog(null)} className="text-white/60">Cancel</Button>
              <Button data-testid="journey-save-entry" onClick={() => save('entry-pathway', { entry_pathway: selected })}
                disabled={saving || !selected} className="bg-emerald-600 hover:bg-emerald-700">
                {saving ? 'Saving...' : 'Update'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Performative Pathway */}
        <Dialog open={dialog === 'perf'} onOpenChange={(o) => !o && setDialog(null)}>
          <DialogContent className="bg-[#1a1a2e] border-white/10 text-white">
            <DialogHeader><DialogTitle>Assign Performative Pathway</DialogTitle></DialogHeader>
            <div className="py-3">
              <p className="text-xs text-white/40 mb-2">Manual assignment only. Should only be assigned after biological baseline is achieved.</p>
              <Select value={selected} onValueChange={setSelected}>
                <SelectTrigger data-testid="journey-perf-select" className="bg-black/30 border-white/10 text-white">
                  <SelectValue placeholder="Select pathway..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a2e] border-white/10">
                  {['SEEDS','ROOTS','ETERNIA'].map(p => (
                    <SelectItem key={p} value={p} className="text-white">{PERF_LABELS[p]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDialog(null)} className="text-white/60">Cancel</Button>
              <Button data-testid="journey-save-perf" onClick={() => save('performative-pathway', { performative_pathway: selected })}
                disabled={saving || !selected} className="bg-violet-600 hover:bg-violet-700">
                {saving ? 'Saving...' : 'Assign'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Therapeutic Toggle */}
        <Dialog open={dialog === 'therapeutic'} onOpenChange={(o) => !o && setDialog(null)}>
          <DialogContent className="bg-[#1a1a2e] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle>{sub.is_therapeutic_active ? 'Deactivate' : 'Activate'} Therapeutic</DialogTitle>
            </DialogHeader>
            <div className="py-3 space-y-3">
              <p className="text-sm text-white/60">
                {sub.is_therapeutic_active
                  ? 'Marks therapeutic as Stabilized. Subscriber continues without active correction focus.'
                  : 'Activates therapeutic correction overlay with terrain repair protocols.'}
              </p>
              {!sub.is_therapeutic_active && (
                <div>
                  <p className="text-xs text-white/40 mb-1">Intensity (optional)</p>
                  <Select value={selected || 'MODERATE'} onValueChange={setSelected}>
                    <SelectTrigger className="bg-black/30 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a2e] border-white/10">
                      <SelectItem value="LOW" className="text-white">Low</SelectItem>
                      <SelectItem value="MODERATE" className="text-white">Moderate</SelectItem>
                      <SelectItem value="HIGH" className="text-white">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDialog(null)} className="text-white/60">Cancel</Button>
              <Button data-testid="journey-save-therapeutic"
                onClick={() => save('therapeutic', {
                  is_therapeutic_active: !sub.is_therapeutic_active,
                  therapeutic_intensity: !sub.is_therapeutic_active ? (selected || 'MODERATE') : null,
                })}
                disabled={saving}
                className={sub.is_therapeutic_active ? 'bg-amber-600 hover:bg-amber-700' : 'bg-rose-600 hover:bg-rose-700'}>
                {saving ? 'Saving...' : sub.is_therapeutic_active ? 'Deactivate' : 'Activate'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export default function JourneyManagement() {
  const { user } = useAuth();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const usersRes = await axios.get(`${API}/api/users`, { headers });
      const subs = (Array.isArray(usersRes.data) ? usersRes.data : usersRes.data.users || []).filter(u => u.role === 'subscriber');
      const journeyData = await Promise.all(
        subs.map(s =>
          axios.get(`${API}/api/journey/subscriber/${s.id}`, { headers })
            .then(r => ({ ...r.data, name: s.name }))
            .catch(() => ({
              subscriber_id: s.id, name: s.name,
              entry_pathway: null, current_mode: 'PREDECODE',
              performative_pathway: null, is_therapeutic_active: false,
              therapeutic_state: 'Stabilized', therapeutic_intensity: null,
              valid_next_modes: [], conditions: [], journey_stage: '', life_stage: '',
            }))
        )
      );
      setSubscribers(journeyData);
    } catch (e) { toast.error('Failed to load journey data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleMigration = async () => {
    setMigrating(true);
    try {
      const res = await axios.post(`${API}/api/journey/migrate-reframed`, {}, { headers });
      toast.success(`Migration: ${res.data.migrated} migrated, ${res.data.already_done} already done`);
      loadData();
    } catch { toast.error('Migration failed'); }
    finally { setMigrating(false); }
  };

  // Group by mode
  const grouped = {};
  ['PREDECODE','DECODE_PENDING','DECODE_COMPLETE','THERAPEUTIC_CORRECTION','BASELINE_ACHIEVED','PERFORMATIVE'].forEach(m => { grouped[m] = []; });
  subscribers.forEach(s => {
    const mode = s.current_mode || 'PREDECODE';
    if (grouped[mode]) grouped[mode].push(s);
    else grouped.PREDECODE.push(s);
  });

  const therapeutic = subscribers.filter(s => s.is_therapeutic_active).length;

  // Mode flow visualization
  const modeFlow = ['PREDECODE','DECODE_PENDING','DECODE_COMPLETE','THERAPEUTIC_CORRECTION','BASELINE_ACHIEVED','PERFORMATIVE'];

  return (
    <AdminLayout>
      <div data-testid="journey-management-page" className="p-6 max-w-7xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Journey Model</h1>
            <p className="text-sm text-white/40 mt-0.5">Dynamic Biological Journey System</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="journey-refresh" variant="outline" size="sm" onClick={loadData} disabled={loading}
              className="border-white/10 text-white/60 hover:text-white">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            {user?.role === 'admin' && (
              <Button data-testid="journey-migrate" variant="outline" size="sm" onClick={handleMigration} disabled={migrating}
                className="border-amber-700/40 text-amber-400 hover:bg-amber-900/30">
                {migrating ? 'Migrating...' : 'Run Migration'}
              </Button>
            )}
          </div>
        </div>

        {/* Mode Flow Visualization */}
        <Card className="bg-[#0d1117] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-1 overflow-x-auto">
              {modeFlow.map((m, i) => {
                const mc = MODE_COLORS[m];
                const count = grouped[m]?.length || 0;
                return (
                  <React.Fragment key={m}>
                    <div className={`flex-1 min-w-[110px] rounded-lg p-2.5 text-center ${mc.bg} border border-white/5`}>
                      <p className={`text-lg font-bold ${mc.text}`}>{count}</p>
                      <p className="text-[9px] text-white/40 leading-tight">{MODE_LABELS[m]}</p>
                    </div>
                    {i < modeFlow.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-white/15 flex-shrink-0" />}
                  </React.Fragment>
                );
              })}
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <Layers className="w-3.5 h-3.5 text-white/30" />
                  <span className="text-[10px] text-white/30">Total: {subscribers.length}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Activity className="w-3.5 h-3.5 text-rose-400/50" />
                  <span className="text-[10px] text-white/30">Therapeutic Active: {therapeutic}</span>
                </div>
              </div>
              <p className="text-[9px] text-white/20">Entry → PreDECODE → 360 DECODE → Therapeutic → Baseline → Performative</p>
            </div>
          </CardContent>
        </Card>

        {/* Subscriber Cards grouped by Mode */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-5 h-5 text-white/20 animate-spin mx-auto" />
            <p className="text-xs text-white/20 mt-2">Loading...</p>
          </div>
        ) : (
          modeFlow.map(mode => {
            const subs = grouped[mode] || [];
            if (subs.length === 0) return null;
            const mc = MODE_COLORS[mode];
            return (
              <div key={mode}>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-2 h-2 rounded-full ${mc.text.replace('text-', 'bg-')}`} />
                  <h2 className={`text-xs font-semibold ${mc.text}`}>{MODE_LABELS[mode]}</h2>
                  <Badge className={`${mc.badge} text-[9px]`}>{subs.length}</Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
                  {subs.map(s => (
                    <SubscriberCard key={s.subscriber_id} sub={s} onRefresh={loadData} />
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    </AdminLayout>
  );
}
